/** client/src/lib/route-context.ts — Route-to-scope-context mapping for smart-bounce logic. */

export type RouteContext = "personal-only" | "org-only" | "both";

interface RouteContextEntry {
  readonly pattern: RegExp | string;
  readonly context: RouteContext;
}

/**
 * ROUTE_CONTEXT_MAP — ordered first-match wins.
 *
 * Specificity rules:
 * 1. More-specific patterns come BEFORE catch-alls (e.g. /onboarding/guardian
 *    before /onboarding/*).
 * 2. All patterns are anchored (^ … $) to avoid partial matches.
 *
 * Spec: wave6.1c-5 spec lines 102–117. R2: /dashboard MUST resolve "both".
 */
export const ROUTE_CONTEXT_MAP: readonly RouteContextEntry[] = [
  // Dashboard (R2 invariant — must be "both"). Covers depth-1 `/dashboard`,
  // depth-2 `/dashboard/:tab?`, and depth-3 `/dashboard/alerts/:clusterId`.
  { pattern: /^\/dashboard(?:\/alerts\/[^/]+|\/[^/]+)?$/, context: "both" },

  // Security centre — personal-only. nav-items.ts's `security` NavItem is
  // already documented as "a personal-only surface (hardware key + personal
  // sensory seeds + personal guardians) — intentionally omitted from the org
  // view"; this was the one place that intent wasn't enforced at the route
  // level, so switching from Personal into an org while sitting on this page
  // silently left the personal dashboard rendered under the org's "Acting
  // as" chip (RouteGuard only bounces personal-only/org-only routes, and
  // this was misclassified "both"). Reclassifying it lets the existing
  // RouteGuard bounce-on-context-change effect handle this automatically,
  // the same mechanism /memories and /guardian-setup already rely on.
  { pattern: "/security-centre", context: "personal-only" },

  // Security Keys — personal-only, same reasoning as /security-centre above.
  // Split off Security Overview into its own route (owner direction,
  // 2026-08-10) as a sibling in the section nav (security-section-tabs.tsx);
  // it inherits the exact same "must bounce, not render under org chrome" need.
  { pattern: "/security-keys", context: "personal-only" },

  // Personal-only: Memories management (sensory-seed review/update). A personal
  // identity anchor like the vault-identifier pages — must bounce out of org
  // context, not render the personal surface inside org chrome.
  { pattern: "/memories", context: "personal-only" },

  // Personal-only: guardian & invite flows — BEFORE /onboarding/* catch-all
  { pattern: "/setup/guardians",                                    context: "personal-only" },
  { pattern: "/guardian-setup",                                     context: "personal-only" },
  { pattern: "/onboarding/guardian",                                context: "personal-only" },

  // Personal-only: vault identifier pages
  // Both /vault/identifiers/:id/history (real inbound link) and
  // /vault-identifier-history/:id (spec-named path) redirect to /audit (spec AC-8);
  // patterns kept so in-flight navigation bounces to personal context before redirect.
  { pattern: "/vault-identifiers",                                               context: "personal-only" },
  { pattern: /^\/vault\/identifiers\/[^/]+\/history$/,                          context: "personal-only" },
  { pattern: /^\/vault-identifier-history(?:\/[^/]+)?$/,                        context: "personal-only" },

  // Org-only: org vault, including its payout sub-routes
  // (/org-vault/payouts, /org-vault/payouts/request/:id,
  // /org-vault/payouts/approve/:id) — all of them read `activeOrgId` off
  // useOrgContext(), and org-vault/payouts.tsx and org-vault/index.tsx render
  // their own "switch to an organisation" empty state when it's null, so a
  // bare string match on "/org-vault" left every sub-route unclassified and
  // defaulting to "both" (same bug class as /deals below).
  { pattern: /^\/org-vault(?:\/.*)?$/, context: "org-only" },

  // Org-only: Deals hub + detail (deals/index.tsx doc comment: "Org-context
  // only: switch to an organisation to see its deals. Personal context shows
  // a short prompt instead"). Previously unclassified, so RouteGuard never
  // bounced off it when switching to personal context, leaving the "Switch to
  // an organisation" empty state stranded under the Personal chip.
  { pattern: /^\/deals(?:\/.*)?$/, context: "org-only" },

  // Org-only: new payment request (challenges/NewPaymentRequest.tsx:49 —
  // `senderOrgId = currentContext === "personal" ? null : activeOrgId`, which
  // disables the submit query and renders the same "Switch to an
  // organisation" empty state). Same unclassified-defaults-to-"both" bug.
  { pattern: "/challenges/new-payment-request", context: "org-only" },

  // Org-only: internal team conversations (ledger #53 — staff-of-an-org only
  // by definition; conversation-thread-group.tsx falls back to a bogus
  // orgId="personal" sentinel when activeOrgId is null rather than an honest
  // empty state, so leaving this unclassified stranded a broken thread read
  // instead of bouncing away). One regex covers both "/new" and "/:id" — a
  // single path segment matches "[^/]+" the same as any other.
  { pattern: /^\/challenges\/conversations\/internal\/[^/]+$/, context: "org-only" },

  // Unified Connections view — both personal and org context
  { pattern: "/connections", context: "both" },

  // Audit (NEW) — both
  { pattern: "/audit", context: "both" },

  // Org-only: auth-requests
  { pattern: "/auth-requests",           context: "org-only" },
  { pattern: /^\/auth-requests\/[^/]+$/, context: "org-only" },

  // Admin platform routes — scope-agnostic ("both"). Platform admins operate
  // with activeOrgId: null (personal context), so these MUST NOT be org-only
  // or RouteGuard bounces them to /dashboard before the page mounts (issue
  // #491). Defense-in-depth:
  //   - AAL3 gate: `<Guarded minAal="AAL3">` in App.tsx (AAL only; NOT role).
  //   - Role gate: server-side ONLY. /api/admin/platform/* requires
  //     `strem_admin` (zero-trust), and /api/admin-platform/* requires
  //     `admin-platform` (require-platform-admin middleware).
  //
  // Trade-off: a non-admin AAL3 user reaching this URL renders the platform
  // chrome with 403'd content. Not a new regression — an org-context AAL3
  // non-admin already had this path; this change only adds personal-context.
  // A proper client-side role gate is tracked as a follow-up issue.
  //
  // Specific before wildcard ordering retained per file convention.
  { pattern: /^\/admin\/platform\/org\/[^/]+(?:\/[^/]*)?$/, context: "both" },
  { pattern: /^\/admin\/platform(?:\/[^/]*)?$/,             context: "both" },

  // Onboarding catch-all — AFTER personal-only overrides above
  { pattern: /^\/onboarding(?:\/.*)?$/, context: "both" },

  // Setup / bespoke-seeding / pathway-status
  { pattern: "/setup",           context: "both" },
  { pattern: "/bespoke-seeding", context: "both" },
  { pattern: "/pathway-status",  context: "both" },

  // Auth / recovery / invite / bank
  // (`/onboarding/counterparty-invite/:token` resolves via the `/onboarding/*` catch-all above.)
  { pattern: "/auth",                                              context: "both" },
  { pattern: "/recovery",                                          context: "both" },
  { pattern: /^\/i\/[^/]+$/,                                       context: "both" },
  { pattern: "/bank/add",                                          context: "both" },

  // Root
  { pattern: "/", context: "both" },
];

/**
 * getRouteContext — returns the scope context for the given runtime path.
 *
 * Iterates ROUTE_CONTEXT_MAP in order; first match wins. Unknown paths
 * default to "both" (spec Q3, line 15).
 */
export function getRouteContext(path: string): RouteContext {
  for (const entry of ROUTE_CONTEXT_MAP) {
    if (typeof entry.pattern === "string") {
      if (path === entry.pattern) return entry.context;
    } else {
      if (entry.pattern.test(path)) return entry.context;
    }
  }
  return "both";
}

/**
 * isPlatformRoute — is this path inside the Platform Console?
 *
 * This file already owns route→context classification, so it owns this
 * predicate too. It exists because the same `startsWith` test was hand-copied
 * to five surfaces (sidebar, bottom tabs, the app-shell header's bell gate, the
 * profile chip, the switcher) that MUST agree — the console is deliberately a
 * "both"-context route (see ROUTE_CONTEXT_MAP above), so nothing else forces
 * them into agreement.
 *
 * They had already diverged: the bell gate tested `"/admin/platform/"` WITH a
 * trailing slash, so the bare `/admin/platform` landing route was classified as
 * platform by four surfaces and non-platform by the fifth. Matching the bare
 * path is the correct reading — it is the console's own entry point.
 */
export function isPlatformRoute(path: string): boolean {
  return path === "/admin/platform" || path.startsWith("/admin/platform/");
}
